// App.tsx
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LocalizationProvider } from "./contexts/LocalizationContext";
import { ThemeProvider } from "./contexts/ThemeContext";

import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import MerchantInfoForm from "./components/MerchantInfoForm";
import UpdateBanner from "./components/UpdateBanner";
import SandboxBanner from "./components/SandboxBanner";
import SandboxScenarios from "./components/SandboxScenarios";
import Dashboard from "./components/Dashboard";
import Transfers from "./components/Transfers";
import Balances from "./components/Balances";
import Reports from "./components/Reports";
import TransactionsReport from "./components/reports/TransactionsReport";
import ReportsOverview from "./components/reports/ReportsOverview";
import Profile from "./components/Profile";
import MerchantAccount from "./components/MerchantAccount";
import TeamManagement from "./components/TeamManagement";
import ChatBot from "./components/ChatBot";
import Settings from "./components/Settings";
import AdvancedAnalytics from "./components/analytics/AdvancedAnalytics";
import PWAInstaller from "./components/PWAInstaller";
import Tickets from "./components/Tickets";
import RoleBasedSidebar from "./components/RoleBasedSidebar";
import RoleBasedHeader from "./components/RoleBasedHeader";
import LoadingScreen from "./components/LoadingScreen";
import DocumentationPage from "./components/DocumentationPage";
import TransferApproval from "./components/transfers/TransferApproval";
import ForcePinReset from "./components/ForcePinReset";
import Withdrawal from "./components/Withdrawal";
import CardTransactions from "./components/CardTransactions";
import WalletHistory from "./components/WalletHistory";
import CardWallets from "./components/CardWallets";
import CashoutSettings from "./components/CashoutSettings";
import CardWithdraw from "./components/CardWithdraw";
import TicketsAnnouncementModal from "./components/TicketsAnnouncementModal";
import WebSocketNotifier from "./components/WebSocketNotifier";
import InactivityGuard from "./components/InactivityGuard";
import { Toaster } from "react-hot-toast";
import { NotificationProvider } from "./contexts/NotificationContext";
import TransferDetails from "./components/transfers/TransferDetails";

/* ---------------------------------------------------
   1. ROUTE PRIVÉE - CORRIGÉE
--------------------------------------------------- */
function PrivateRoute({ children }: { children: JSX.Element }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Chargement de votre session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/* ---------------------------------------------------
   2. LAYOUT AUTHENTIFIÉ - OPTIMISÉ POUR MOBILE
--------------------------------------------------- */
const DashboardLayout: React.FC = () => {
  const { logout, user, loading, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("dashboard");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true);
  const [showInfoForm, setShowInfoForm] = React.useState<boolean | null>(null);

  // Heartbeat - sends every 2 min while authenticated
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const sessionToken = localStorage.getItem("session_token");
    if (!sessionToken) return;

    const send = () => import('./services/authService').then(m => m.sendHeartbeat()).catch(() => {});
    send();
    const interval = setInterval(send, 120_000);

    // Handle browser close
    const handleUnload = () => {
      const token = localStorage.getItem("session_token");
      if (token) {
        const body = JSON.stringify({ session_token: token, reason: "browser_close" });
        navigator.sendBeacon(
          `${import.meta.env.VITE_API_URL || "http://localhost:3944"}/sessions/end`,
          new Blob([body], { type: "application/json" })
        );
      }
    };
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [isAuthenticated]);

  // Check if merchant needs to fill info form (all merchants)
  React.useEffect(() => {
    const checkInfoVerified = async () => {
      if (!user) return;
      try {
        const { default: api } = await import('./services/api');
        const res = await api.get('/dashboard/institution-info');
        setShowInfoForm(res.data.info_verified === 0 || res.data.info_verified === false);
      } catch {
        setShowInfoForm(false);
      }
    };
    if (user && showInfoForm === null) checkInfoVerified();
  }, [user]);

  // Afficher le loader si en cours de chargement ou pas d'utilisateur
  if (loading || !user || showInfoForm === null) {
    return <LoadingScreen message="Chargement de votre espace..." />;
  }

  // Show merchant info form if needed
  if (showInfoForm) {
    return <MerchantInfoForm onComplete={() => setShowInfoForm(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 flex flex-col relative">
      <WebSocketNotifier />
      <InactivityGuard />
      {/* Mobile overlay with smooth fade */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Header desktop fixe (offset top-9 pour laisser le SandboxBanner h-9 dessus) */}
      <div className="hidden lg:block fixed top-9 left-0 right-0 z-30">
        <RoleBasedHeader
          isSidebarCollapsed={isSidebarCollapsed}
          sidebarWidth={isSidebarCollapsed ? 'w-20' : 'w-64'}
        />
      </div>

      {/* Header mobile - fixe en haut (offset top-9 pour laisser le SandboxBanner h-9 dessus) */}
      <div className="lg:hidden fixed top-9 left-0 right-0 z-40 h-16">
        <RoleBasedHeader
          isMobile={true}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
      </div>

      {/* Sidebar mobile - slide depuis la gauche */}
      <div
        className={`
          fixed top-9 bottom-0 left-0 z-50 transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
          w-64
          lg:hidden
        `}
      >
        <RoleBasedSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNavigate={() => setIsMobileMenuOpen(false)}
          logout={logout}
          isCollapsed={false}
          onToggle={() => {}}
        />
      </div>

      {/* Sidebar desktop - toujours visible */}
      <div
        className={`
          hidden lg:block fixed top-9 bottom-0 left-0 z-30
          transition-all duration-500 ease-out
          ${isSidebarCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        <RoleBasedSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNavigate={() => {}}
          logout={logout}
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {/* Main content area */}
      <div className={`
        flex-1 min-h-screen w-full
        transition-all duration-500 ease-out
        ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}
        ${isMobileMenuOpen ? 'overflow-hidden' : ''}
        pt-[100px] lg:pt-[100px]
      `}>
        {/* PWA Installer - desktop only */}
        <div className="hidden lg:block px-4 lg:px-6 pt-4">
          <PWAInstaller />
        </div>

        {/* Main content with page transition */}
        <main className="p-3 sm:p-4 lg:p-6 page-enter">
          <Routes>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="withdrawal" element={<Withdrawal />} />
            <Route path="transfers" element={<Transfers />} />
            <Route path="/transfer-details/:reference" element={<TransferDetails />} />
            <Route path="/transfer-approval" element={<TransferApproval />} />
            <Route path="balances" element={<Balances />} />
            
            {/* Routes Card Management */}
            <Route path="cards" element={<Navigate to="cards/balances" replace />} />
            <Route path="cards/balances" element={<CardWallets />} /> {/* NOUVEAU */}
            <Route path="cards/transactions" element={<CardTransactions />} />
            <Route path="cards/history" element={<WalletHistory />} />
            {/* Bookmark redirect — old card-themed cashout settings is now unified under /cashout-settings */}
            <Route path="cards/withdrawal-settings" element={<Navigate to="/cashout-settings" replace />} />
            <Route path="cashout-settings" element={<CashoutSettings />} />
            <Route path="cards/withdraw" element={<CardWithdraw />} />
            
            {/* Routes Reports avec sous-routes */}
            <Route path="reports" element={<Reports />}>
              <Route path="overview" element={<ReportsOverview />} />
              <Route path="transactions" element={<TransactionsReport />} />
              {/* Backwards compat redirects */}
              <Route path="daily" element={<Navigate to="/reports/overview" replace />} />
              <Route path="custom" element={<Navigate to="/reports/transactions" replace />} />
              <Route path="export" element={<Navigate to="/reports/transactions" replace />} />
              <Route index element={<Navigate to="overview" replace />} />
            </Route>
            
            <Route path="profile" element={<Profile />} />
            <Route path="team" element={<TeamManagement />} />
            <Route path="account" element={<MerchantAccount />} />
            <Route path="settings" element={<Settings />} />
            <Route path="analytics" element={<AdvancedAnalytics />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="reset-pin" element={<ForcePinReset />} />
            <Route path="docs" element={<DocumentationPage />} />
            
            {/* Redirection par défaut vers dashboard */}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </main>
      </div>

      {/* ChatBot - position ajustée pour mobile */}
      <ChatBot />

      {/* One-time announcement for the new ticketing system. */}
      <TicketsAnnouncementModal />
    </div>
  );
};

/* ---------------------------------------------------
   3. LOGIN WRAPPER - CORRIGÉ
--------------------------------------------------- */
const LoginPageWrapper: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, loading, navigate]);

  // Afficher le loader pendant la vérification de l'authentification
  if (loading) {
    return <LoadingScreen message="Vérification de votre session..." />;
  }

  return <Login />;
};

/* ---------------------------------------------------
   4. APP PRINCIPALE - STRUCTURE SIMPLIFIÉE
--------------------------------------------------- */
function App() {
  return (
    <ThemeProvider>
      <LocalizationProvider>
        <AuthProvider>
          <NotificationProvider> 
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 5000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: 'green',
                  secondary: 'black',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: 'red',
                  secondary: 'black',
                },
              },
            }}
          />
          <SandboxBanner />
          <UpdateBanner />
          <Router>
            <Routes>
              {/* Route login publique */}
              <Route path="/login" element={<LoginPageWrapper />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/sandbox/scenarios" element={<SandboxScenarios />} />
              
              {/* Routes protégées */}
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <DashboardLayout />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Router>
          </NotificationProvider> 
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;